import React, { useState, useEffect } from 'react';
import {
    Text,
    Heading,
    Badge,
    Icon,
    Button,
    Container
} from './ui/index.jsx';

// AnalyzePromptGenerator React component for professional roadmap interface
// Converted from vanilla JS to React + Vite - COMPLETE with all original features
const AnalyzePromptGenerator = ({ promptData, onError, onRetry, batchProgress = null }) => {
    const [currentPrompts, setCurrentPrompts] = useState({});
    const [currentPlatform, setCurrentPlatform] = useState('cursor');
    const [expandedSteps, setExpandedSteps] = useState(new Set());
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copiedSteps, setCopiedSteps] = useState(new Set());
    const [showModal, setShowModal] = useState(false);
    const [modalPrompt, setModalPrompt] = useState(null);
    const [toasts, setToasts] = useState([]);

    // Platform-specific icon mapping
    const platformIcons = {
        'base44': 'lightbulb',
        'cursor': 'cursor',
        'replit': 'lightning',
        'loveable': 'heart',
        'default': 'rocket'
    };

    // Convert new microPrompts array format to legacy prompts object format
    const convertMicroPromptsToLegacy = (microPromptsData) => {

        if (!microPromptsData?.microPrompts || !Array.isArray(microPromptsData.microPrompts)) {
            return null;
        }

        const legacyPrompts = {};
        // Prefer backend-provided metrics/metadata for confidence
        const topLevelConfidence = (typeof microPromptsData.metrics?.overallConfidence === 'number'
            ? microPromptsData.metrics.overallConfidence
            : (typeof microPromptsData.metadata?.claudeConfidence === 'number'
                ? microPromptsData.metadata.claudeConfidence
                : (typeof microPromptsData.confidence === 'number' ? microPromptsData.confidence : undefined)));

        microPromptsData.microPrompts.forEach((microPrompt, index) => {
            const stepId = microPrompt.id || `step-${index + 1}`;

            legacyPrompts[stepId] = {
                id: microPrompt.id || stepId,
                title: microPrompt.title || `Step ${index + 1}`,
                prompt: microPrompt.cursorPrompt?.command || microPrompt.prompt || microPrompt.title || 'No prompt available',
                description: microPrompt.cursorPrompt?.context || microPrompt.description || 'Implementation task',
                explanation: microPrompt.goal ? `Goal: ${microPrompt.goal}. ${microPrompt.lldReference?.specification || ''}` : microPrompt.lldReference?.specification || '',
                aftercare: [
                    ...(microPrompt.cursorPrompt?.terminalCommands || []),
                    ...(microPrompt.cursorPrompt?.aiTips || []),
                    ...(microPrompt.deliverables || [])
                ],
                validationCriteria: [
                    ...(microPrompt.validation?.functionalChecks || []),
                    ...(microPrompt.validation?.performanceChecks || []),
                    ...(microPrompt.validation?.securityChecks || [])
                ],
                estimatedTime: microPrompt.estimatedTime || 'Not specified',
                difficulty: microPrompt.estimatedComplexity === 'simple' ? 'Easy' :
                    microPrompt.estimatedComplexity === 'complex' ? 'Hard' : 'Medium',
                confidence: (typeof microPrompt.confidence === 'number'
                    ? microPrompt.confidence
                    : (typeof topLevelConfidence === 'number' ? topLevelConfidence : 0.85)),
                dependencies: microPrompt.prerequisitePrompts || [],
                outputs: microPrompt.deliverables || [],
                category: microPrompt.category || 'implementation',
                priority: microPrompt.priority || 'important',
                followupCommand: microPrompt.cursorPrompt?.followupCommand || '',
                terminalCommands: microPrompt.cursorPrompt?.terminalCommands || [],
                implementationDetails: microPrompt.implementationDetails || {},
                qualityRequirements: microPrompt.qualityRequirements || {}
            };
        });

        return legacyPrompts;
    };

    // Process prompt data on mount or data change
    useEffect(() => {
        if (!promptData) {
            setError('No prompt data available');
            setIsLoading(false);
            return;
        }


        let processedPrompts = null;
        let detectedPlatform = 'cursor';

        if (promptData.microPrompts && Array.isArray(promptData.microPrompts)) {
            processedPrompts = convertMicroPromptsToLegacy(promptData);
            detectedPlatform = promptData.metadata?.userPlatform || promptData.platform || 'cursor';
        }
        else if (promptData.data?.microPrompts && Array.isArray(promptData.data.microPrompts)) {
            processedPrompts = convertMicroPromptsToLegacy(promptData.data);
            detectedPlatform = promptData.data.metadata?.userPlatform || promptData.platform || 'cursor';
        }
        else if (promptData.prompts && typeof promptData.prompts === 'object') {
            processedPrompts = promptData.prompts;
            detectedPlatform = promptData.platform || 'cursor';
        }
        // Fallback: Direct prompts data
        else if (typeof promptData === 'object' && !Array.isArray(promptData)) {
            // Check if promptData itself contains prompt-like structure
            const hasPromptStructure = Object.values(promptData).some(value =>
                value && typeof value === 'object' && (value.title || value.prompt)
            );

            if (hasPromptStructure) {
                processedPrompts = promptData;
            }
        }

        if (!processedPrompts || Object.keys(processedPrompts).length === 0) {
            setError('No valid prompts found in the provided data');
            setIsLoading(false);
            return;
        }

        setCurrentPrompts(processedPrompts);
        setCurrentPlatform(detectedPlatform);
        setIsLoading(false);
        setError(null);

    }, [promptData]);

    // Toggle step expansion
    const toggleStep = (stepId) => {
        setExpandedSteps(prev => {
            const newSet = new Set(prev);
            if (newSet.has(stepId)) {
                newSet.delete(stepId);
            } else {
                newSet.add(stepId);
            }
            return newSet;
        });
    };

    // Toggle all prompts
    const toggleAllPrompts = () => {
        const allSteps = Object.keys(currentPrompts);
        if (expandedSteps.size === allSteps.length) {
            // All expanded, collapse all
            setExpandedSteps(new Set());
        } else {
            // Expand all
            setExpandedSteps(new Set(allSteps));
        }
    };

    // Enhanced copy prompt with context (RESTORED ORIGINAL FUNCTIONALITY)
    const copyPrompt = async (stepId) => {
        const alreadyCopied = copiedSteps.has(stepId);
        const promptData = currentPrompts[stepId];
        if (!promptData?.prompt) return;

        const cleanedPrompt = cleanPromptText(promptData.prompt);

        // Add context and instructions for better AI interaction (ORIGINAL FEATURE)
        const promptWithContext = `${cleanedPrompt}

=== CONTEXT ===
Platform: ${currentPlatform}
Difficulty: ${promptData.difficulty || 'Medium'}
Estimated Time: ${promptData.estimatedTime || 'N/A'}

=== WHAT TO DO AFTER ===
${promptData.aftercare ? promptData.aftercare.map((step, i) => `${i + 1}. ${step}`).join('\n') : 'Follow standard completion practices'}

=== SUCCESS CRITERIA ===
${promptData.validationCriteria ? promptData.validationCriteria.map(criteria => `☐ ${criteria}`).join('\n') : 'Verify functionality works as expected'}`;

        try {
            await navigator.clipboard.writeText(promptWithContext);
            // Persist copied marker and keep it green
            setCopiedSteps(prev => new Set([...prev, stepId]));
            if (alreadyCopied) {
                showToast('Note: You already copied this prompt. Copied again.', 'info');
            } else {
                showToast('Prompt copied with context!', 'success');
            }
        } catch (error) {
            showToast('Copy failed. Please try again.', 'error');
            if (onError) onError('Failed to copy prompt to clipboard');
        }
    };

    // Clean prompt text
    const cleanPromptText = (prompt) => {
        if (!prompt) return '';

        let cleanedPrompt = prompt.trim();

        // Remove only "Tell Base44:" prefix (ORIGINAL LOGIC)
        if (cleanedPrompt.startsWith('Tell Base44:')) {
            cleanedPrompt = cleanedPrompt.substring('Tell Base44:'.length).trim();
        }

        return cleanedPrompt;
    };

    // Show prompt in modal (RESTORED ORIGINAL FUNCTIONALITY)
    const showPromptModal = (promptData) => {
        setModalPrompt(promptData);
        setShowModal(true);
    };

    // Toast notification system (RESTORED ORIGINAL FUNCTIONALITY)
    const showToast = (message, type = 'success') => {
        const id = Date.now();
        const toast = { id, message, type };

        setToasts(prev => [...prev, toast]);

        // Remove after 3 seconds
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 3000);
    };

    // Get difficulty badge variant
    const getDifficultyVariant = (difficulty) => {
        switch (difficulty?.toLowerCase()) {
            case 'easy': return 'secondary';
            case 'medium': return 'secondary';
            case 'hard': return 'secondary';
            default: return 'secondary';
        }
    };

    // Get confidence badge variant
    const getConfidenceVariant = (confidence) => {
        if (confidence >= 90) return 'secondary';
        if (confidence >= 75) return 'secondary';
        if (confidence >= 60) return 'secondary';
        return 'secondary';
    };

    // Enhanced export prompts functionality (RESTORED ORIGINAL FUNCTIONALITY)
    const exportPrompts = async (format) => {
        try {
            // Session-based export (ORIGINAL FEATURE)
            const sessionId = window.getSessionId ? window.getSessionId() : null;
            let sessionData = null;

            if (sessionId) {
                try {
                    const sessionResponse = await fetch(`/api/v2/session?id=${sessionId}`, {
                        credentials: 'include'
                    });
                    if (sessionResponse.ok) {
                        const response = await sessionResponse.json();
                        sessionData = response.session;
                    }
                } catch (error) {
                }
            }

            const data = Object.entries(currentPrompts).map(([id, prompt]) => ({
                id,
                title: prompt.title,
                prompt: cleanPromptText(prompt.prompt),
                description: prompt.description,
                estimatedTime: prompt.estimatedTime,
                difficulty: prompt.difficulty,
                explanation: prompt.explanation,
                aftercare: prompt.aftercare,
                validationCriteria: prompt.validationCriteria
            }));

            let content, filename, mimeType;

            if (format === 'json') {
                content = JSON.stringify(sessionData?.prompts || { prompts: currentPrompts }, null, 2);
                filename = `${currentPlatform}-prompts.json`;
                mimeType = 'application/json';
            } else if (format === 'txt') {
                content = data.map(item =>
                    `=== ${item.title} ===\n${item.description}\n\n${item.prompt}\n\n---\n`
                ).join('\n');
                filename = `${currentPlatform}-prompts.txt`;
                mimeType = 'text/plain';
            } else if (format === 'md') {
                // Enhanced markdown with session info (ORIGINAL FEATURE)
                content = `# ${currentPlatform.charAt(0).toUpperCase() + currentPlatform.slice(1)} Development Prompts\n\n`;
                if (sessionData) {
                    content += `**Project:** ${sessionData.idea}\n`;
                    content += `**Platform:** ${sessionData.platform}\n\n`;
                }
                content += data.map(item =>
                    `## ${item.title}\n\n**Description:** ${item.description}\n\n**Estimated Time:** ${item.estimatedTime}\n\n**Difficulty:** ${item.difficulty}\n\n### Prompt\n\n\`\`\`\n${item.prompt}\n\`\`\`\n\n${item.explanation ? `### Explanation\n\n${item.explanation}\n\n` : ''}${item.aftercare && item.aftercare.length > 0 ? `### Aftercare\n\n${item.aftercare.map(step => `- ${step}`).join('\n')}\n\n` : ''}---\n\n`
                ).join('');
                filename = `${currentPlatform}-development-prompts.md`;
                mimeType = 'text/markdown';
            } else if (format === 'cursor' || format === 'claude') {
                // Platform-specific formats (RESTORED ORIGINAL FUNCTIONALITY)
                content = data.map(item => {
                    let platformPrompt = item.prompt;
                    if (format === 'cursor') {
                        platformPrompt = `// ${item.title}\n// ${item.description}\n\n${item.prompt}`;
                    } else if (format === 'claude') {
                        platformPrompt = `Human: ${item.title}\n\n${item.description}\n\n${item.prompt}\n\nAssistant: I'll help you with ${item.title.toLowerCase()}. Let me implement this step by step.`;
                    }
                    return platformPrompt;
                }).join('\n\n---\n\n');
                filename = `${currentPlatform}-${format}-prompts.txt`;
                mimeType = 'text/plain';
            }

            // Create and download file
            const blob = new Blob([content], { type: mimeType });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            showToast(`Prompts exported as ${format.toUpperCase()}!`, 'success');
        } catch (error) {
            showToast('Export failed. Please try again.', 'error');
            if (onError) onError('Failed to export prompts');
        }
    };

    // Share functionality (RESTORED ORIGINAL FUNCTIONALITY)
    const sharePrompts = () => {
        if (navigator.share) {
            navigator.share({
                title: 'AI Development Prompts - The Architect',
                text: 'Check out these AI-generated development prompts!',
                url: window.location.href
            }).catch((error) => {
            });
        } else {
            // Fallback: copy URL to clipboard
            navigator.clipboard.writeText(window.location.href)
                .then(() => showToast('URL copied to clipboard!', 'success'))
                .catch(() => showToast('Unable to share', 'error'));
        }
    };

    // Handle retry (RESTORED ORIGINAL FUNCTIONALITY)
    const handleRetry = () => {
        setError(null);
        if (onRetry) {
            onRetry();
        } else {
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#7B61FF] border-t-transparent"></div>
                <Text variant="secondary" className="ml-3">Loading prompts...</Text>
            </div>
        );
    }

    // Error state with retry (RESTORED ORIGINAL FUNCTIONALITY)
    if (error) {
        return (
            <div className="glass-panel p-8 text-center rounded-2xl">
                <Icon name="warning" size="2xl" className="mb-4 mx-auto text-[#FF6B6B]" />
                <Heading level={3} className="text-lg mb-2 text-white">Prompt Generator Error</Heading>
                <Text variant="secondary" className="mb-4 text-gray-300">{error}</Text>
                <Button
                    onClick={handleRetry}
                    variant="primary"
                    className="flex items-center justify-center bg-[#7B61FF] hover:bg-[#6b51eb] text-white px-6 py-2 rounded-xl"
                >
                    <Icon name="refresh" size="xs" className="mr-2" />
                    Retry
                </Button>
            </div>
        );
    }

    if (!promptData || Object.keys(currentPrompts).length === 0) {
        return null;
    }

    return (
        <div className="space-y-6">
            {/* Batch Progress Display */}
            {batchProgress && batchProgress.isGenerating && (
                <div className="glass-panel p-6 rounded-2xl border border-[#7B61FF]/20 bg-gradient-to-r from-[#7B61FF]/5 to-[#9333EA]/5">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-3 border-[#7B61FF] border-t-transparent mr-3"></div>
                            <Heading level={3} className="text-lg text-white">
                                Generating Production-Ready Prompts...
                            </Heading>
                        </div>
                        <Badge variant="primary" className="bg-[#7B61FF]/20 text-[#BA87FF] border-[#7B61FF]/30">
                            Batch {batchProgress.currentBatch + 1} of {batchProgress.totalBatches}
                        </Badge>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4">
                        <div className="w-full bg-black/40 rounded-full h-3 overflow-hidden border border-white/5">
                            <div
                                className="bg-gradient-to-r from-[#7B61FF] to-[#9333EA] h-full transition-all duration-500 ease-out shadow-[0_0_10px_rgba(123,97,255,0.5)]"
                                style={{ width: `${(batchProgress.completedPrompts / batchProgress.targetPrompts) * 100}%` }}
                            />
                        </div>
                        <div className="flex justify-between mt-2">
                            <Text variant="secondary" size="sm" className="text-gray-400">
                                {batchProgress.completedPrompts} of {batchProgress.targetPrompts} prompts generated
                            </Text>
                            {batchProgress.estimatedTimeRemaining && (
                                <Text variant="secondary" size="sm" className="text-gray-400">
                                    ~{batchProgress.estimatedTimeRemaining}s remaining
                                </Text>
                            )}
                        </div>
                    </div>

                    {/* Batch Status Grid */}
                    <div className="grid grid-cols-5 gap-2">
                        {batchProgress.batchStatus.map((status, index) => (
                            <div
                                key={index}
                                className={`h-2 rounded-full transition-all duration-300 ${status === 'completed' ? 'bg-[#7B61FF] shadow-[0_0_5px_rgba(123,97,255,0.5)]' :
                                    status === 'processing' ? 'bg-[#FFB800] animate-pulse' :
                                        'bg-white/10'
                                    }`}
                                title={`Batch ${index + 1}: ${status}`}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Hero Section */}
            <PromptHeroSection
                promptData={promptData}
                currentPlatform={currentPlatform}
                platformIcons={platformIcons}
                currentPrompts={currentPrompts}
                onToggleAll={toggleAllPrompts}
                onExport={exportPrompts}
                onShare={sharePrompts}
                expandedCount={expandedSteps.size}
                totalCount={Object.keys(currentPrompts).length}
            />

            {/* Roadmap Steps */}
            <div className="space-y-4">
                {(() => {

                    const orderedPromptIds = promptData.metadata?.recommendedOrder || Object.keys(currentPrompts);
                    const criticalPath = promptData.metadata?.criticalPath || [];


                    return orderedPromptIds.map((promptId, index) => {
                        // Fix key mismatch: convert kebab-case to camelCase
                        const camelCaseId = promptId.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                        const prompt = currentPrompts[promptId] || currentPrompts[camelCaseId];

                        if (!prompt) {
                            return null;
                        }

                        const isCritical = criticalPath.includes(promptId);
                        const stepNumber = index + 1;
                        const actualId = currentPrompts[promptId] ? promptId : camelCaseId;
                        const isExpanded = expandedSteps.has(actualId);
                        const isCopied = copiedSteps.has(actualId);

                        return (
                            <RoadmapStep
                                key={actualId}
                                promptData={prompt}
                                stepNumber={stepNumber}
                                isCritical={isCritical}
                                isExpanded={isExpanded}
                                isCopied={isCopied}
                                onToggle={() => toggleStep(actualId)}
                                onCopy={() => copyPrompt(actualId)}
                                onShowModal={() => showPromptModal(prompt)}
                                getDifficultyColor={getDifficultyVariant}
                                getConfidenceColor={getConfidenceVariant}
                                promptsById={currentPrompts}
                                cleanPromptText={cleanPromptText}
                            />
                        );
                    });
                })()}
            </div>

            {/* Modal (RESTORED ORIGINAL FUNCTIONALITY) */}
            {showModal && modalPrompt && (
                <PromptModal
                    promptData={modalPrompt}
                    platform={currentPlatform}
                    onClose={() => setShowModal(false)}
                    onCopy={() => copyPrompt(modalPrompt.id)}
                    cleanPromptText={cleanPromptText}
                    getConfidenceColor={getConfidenceVariant}
                />
            )}

            {/* Toast Notifications (RESTORED ORIGINAL FUNCTIONALITY) */}
            <ToastContainer toasts={toasts} />
        </div>
    );
};

// Hero Section Component
const PromptHeroSection = ({
    promptData,
    currentPlatform,
    platformIcons,
    currentPrompts,
    onToggleAll,
    onExport,
    onShare,
    expandedCount,
    totalCount
}) => {
    const { metadata } = promptData;
    const unifiedConfidence0To1_HERO = (promptData?.confidence || promptData?.metadata?.claudeConfidence || 0.85);
    const confidenceScore = Math.round(unifiedConfidence0To1_HERO * 100);
    const promptCount = Object.keys(currentPrompts).length;
    const totalTime = metadata?.totalEstimatedTime || promptData?.metrics?.totalEstimatedTime || 'Calculating…';

    return (

        <div className="glass-panel p-6 rounded-2xl relative overflow-hidden group">
            {/* Ambient Shine Effect */}
            <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/5 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" style={{ transform: 'rotate(45deg) translate(-50%, -50%) scale(2)' }} />

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 space-y-4 sm:space-y-0 relative z-10">
                <div className="flex items-center space-x-4">
                    <div className="bg-[#7B61FF]/20 p-3 rounded-xl border border-[#7B61FF]/30 backdrop-blur-sm shadow-[0_0_15px_rgba(123,97,255,0.3)]">
                        <Icon
                            name={platformIcons[currentPlatform] || platformIcons.default}
                            size="2xl"
                            className="text-[#BA87FF]"
                        />
                    </div>
                    <div>
                        <Heading level={2} className="text-xl sm:text-2xl text-white">
                            {currentPlatform.charAt(0).toUpperCase() + currentPlatform.slice(1)} Development Roadmap
                        </Heading>
                        <Text variant="secondary" size="sm" className="sm:text-base text-gray-300">AI-powered prompts for building your application</Text>
                    </div>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
                    <Button
                        onClick={onToggleAll}
                        variant="secondary"
                        size="sm"
                        className="flex items-center justify-center bg-white/5 hover:bg-white/10 border-white/10 text-gray-300 hover:text-white"
                    >
                        <Icon name="clipboard" size="xs" className="mr-1" />
                        {expandedCount === totalCount ? 'Collapse All' : 'Expand All'}
                    </Button>
                    <Button
                        onClick={onShare}
                        variant="primary"
                        size="sm"
                        className="flex items-center justify-center bg-[#7B61FF] hover:bg-[#6b51eb] text-white shadow-[0_0_15px_rgba(123,97,255,0.3)] hover:shadow-[0_0_20px_rgba(123,97,255,0.5)] border-none"
                    >
                        <Icon name="arrowUp" size="xs" className="mr-1" />
                        Share
                    </Button>
                    <ExportDropdown onExport={onExport} />
                </div>
            </div>

            {/* Confidence and Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 relative z-10">
                <div className="bg-black/40 border border-white/10 rounded-xl p-4 text-center hover:bg-white/5 transition-colors">
                    <Text variant="primary" size="xl" weight="bold" className="text-white">{confidenceScore}%</Text>
                    <Text variant="secondary" size="sm" className="text-gray-400">Confidence</Text>
                    <div className="w-full bg-white/10 rounded-full h-1.5 mt-2 overflow-hidden">
                        <div
                            className="bg-[#7B61FF] h-full rounded-full transition-all duration-500 shadow-[0_0_10px_rgba(123,97,255,0.5)]"
                            style={{ width: `${confidenceScore}%` }}
                        ></div>
                    </div>
                </div>
                <div className="bg-black/40 border border-white/10 rounded-xl p-4 text-center hover:bg-white/5 transition-colors">
                    <Text variant="primary" size="xl" weight="bold" className="text-white">{promptCount}</Text>
                    <Text variant="secondary" size="sm" className="text-gray-400">Total Prompts</Text>
                </div>
                <div className="bg-black/40 border border-white/10 rounded-xl p-4 text-center hover:bg-white/5 transition-colors">
                    <Text variant="primary" size="xl" weight="bold" className="text-white">{totalTime}</Text>
                    <Text variant="secondary" size="sm" className="text-gray-400">Est. Time</Text>
                </div>
                <div className="bg-black/40 border border-white/10 rounded-xl p-4 text-center hover:bg-white/5 transition-colors">
                    <Text variant="primary" size="xl" weight="bold" className="text-white">{promptCount}</Text>
                    <Text variant="secondary" size="sm" className="text-gray-400">Phases</Text>
                </div>
            </div>
        </div>
    );

};

// Enhanced Export Dropdown Component (RESTORED ALL ORIGINAL FORMATS)
const ExportDropdown = ({ onExport }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="relative">
            <Button
                onClick={() => setIsOpen(!isOpen)}
                variant="secondary"
                size="sm"
                className="flex items-center justify-center bg-white/5 hover:bg-white/10 border-white/10 text-gray-300 hover:text-white"
            >
                <Icon name="download" size="xs" className="mr-1" />
                Export
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
            </Button>

            {isOpen && (
                <div className="glass-panel absolute right-0 mt-2 w-48 shadow-lg z-50 rounded-xl overflow-hidden border border-white/10">
                    <div className="py-1">
                        <Button
                            onClick={() => { onExport('json'); setIsOpen(false); }}
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start text-left px-4 py-2 hover:bg-white/10 text-gray-300 hover:text-white"
                        >
                            Export as JSON
                        </Button>
                        <Button
                            onClick={() => { onExport('md'); setIsOpen(false); }}
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start text-left px-4 py-2 hover:bg-white/10 text-gray-300 hover:text-white"
                        >
                            Export as Markdown
                        </Button>
                        <Button
                            onClick={() => { onExport('txt'); setIsOpen(false); }}
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start text-left px-4 py-2 hover:bg-white/10 text-gray-300 hover:text-white"
                        >
                            Export as Text
                        </Button>
                        <Button
                            onClick={() => { onExport('cursor'); setIsOpen(false); }}
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start text-left px-4 py-2 hover:bg-white/10 text-gray-300 hover:text-white"
                        >
                            Export for Cursor
                        </Button>
                        <Button
                            onClick={() => { onExport('claude'); setIsOpen(false); }}
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start text-left px-4 py-2 hover:bg-white/10 text-gray-300 hover:text-white"
                        >
                            Export for Claude
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Individual Roadmap Step Component
const RoadmapStep = ({
    promptData,
    stepNumber,
    isCritical,
    isExpanded,
    isCopied,
    onToggle,
    onCopy,
    onShowModal,
    getDifficultyColor,
    getConfidenceColor,
    promptsById,
    cleanPromptText
}) => {
    const {
        id, title, description, prompt, explanation,
        aftercare, estimatedTime, difficulty,
        dependencies, outputs, validationCriteria,
        layer, implementationDetails, qualityRequirements
    } = promptData;

    const unifiedConfidence0To1_CARD = (promptData?.confidence || promptData?.metadata?.claudeConfidence || 0.85);
    const confidenceScore = Math.round(unifiedConfidence0To1_CARD * 100);

    return (
        <div className="roadmap-step">
            {/* Step Header */}
            <div
                className={`glass-panel rounded-t-2xl p-4 sm:p-5 cursor-pointer hover:bg-white/5 transition-all duration-200 border-b-0 ${isExpanded
                    ? 'border-l-2 border-l-[#7B61FF]'
                    : 'border-l border-l-white/5'
                    }`}
                onClick={onToggle}
            >
                {/* Mobile Layout */}
                <div className="block sm:hidden">
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start space-x-3 flex-1 min-w-0">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 transition-colors duration-200 ${isExpanded
                                ? 'bg-[#7B61FF] border border-[#7B61FF] shadow-[0_0_10px_rgba(123,97,255,0.4)]'
                                : 'bg-white/5 border border-white/10 text-gray-400'
                                }`}>
                                {stepNumber}
                            </div>
                            <div className="min-w-0 flex-1">
                                <Heading level={3} className="text-base font-semibold mb-1 break-words text-white">{title}</Heading>
                                <Text variant="secondary" size="sm" className="break-words leading-relaxed text-gray-400">{description}</Text>
                            </div>
                        </div>
                        <div className="ml-2 flex-shrink-0">
                            <Icon
                                name="arrowUp"
                                size="sm"
                                className={`transform transition-all duration-200 ${isExpanded
                                    ? 'rotate-180 text-[#7B61FF]'
                                    : 'text-gray-500'
                                    }`}
                            />
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {isCritical && (
                            <Badge variant="secondary" size="xs">
                                Critical Path
                            </Badge>
                        )}
                        <Badge variant={getDifficultyColor(difficulty)} size="xs">
                            {difficulty || 'Medium'}
                        </Badge>
                        <Badge variant={getConfidenceColor(confidenceScore)} size="xs">
                            {confidenceScore}%
                        </Badge>
                        {/* Removed per-step estimated time on mobile */}
                    </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden sm:flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold flex-shrink-0 transition-colors duration-200 ${isExpanded
                            ? 'bg-[#7B61FF] border border-[#7B61FF] shadow-[0_0_10px_rgba(123,97,255,0.4)]'
                            : 'bg-white/5 border border-white/10 text-gray-400'
                            }`}>
                            {stepNumber}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center space-x-3 mb-1">
                                <Heading level={3} className="text-lg font-semibold break-words text-white">{title}</Heading>
                                {layer && (
                                    <Badge variant="secondary" size="xs" className="flex-shrink-0">
                                        {String(layer).charAt(0).toUpperCase() + String(layer).slice(1)}
                                    </Badge>
                                )}
                                {isCritical && (
                                    <Badge variant="secondary" size="xs" className="flex-shrink-0">
                                        Critical Path
                                    </Badge>
                                )}
                            </div>
                            <Text variant="secondary" size="sm" className="break-words">{description}</Text>
                        </div>
                    </div>
                    <div className="flex items-center space-x-3 flex-shrink-0">
                        {/* Removed per-step estimated time on desktop */}
                        <div className="flex items-center space-x-2">
                            <Badge variant={getDifficultyColor(difficulty)} size="xs" className="bg-white/5 border-white/10 text-gray-300">
                                {difficulty || 'Medium'}
                            </Badge>
                            <Badge variant={getConfidenceColor(confidenceScore)} size="xs" className="bg-[#7B61FF]/10 border-[#7B61FF]/20 text-[#BA87FF]">
                                {confidenceScore}%
                            </Badge>
                        </div>
                        <Icon
                            name="arrowUp"
                            size="sm"
                            className={`transform transition-all duration-200 ${isExpanded
                                ? 'rotate-180 text-[#7B61FF]'
                                : 'text-gray-500'
                                }`}
                        />
                    </div>
                </div>
            </div>

            {/* Step Content (Expandable) */}
            {isExpanded && (
                <div className="bg-black/40 border-l-2 border-r border-b border-l-[#7B61FF] border-r-white/10 border-b-white/10 rounded-b-2xl backdrop-blur-md">{/* Left purple accent border */}
                    {/* AI Prompt Section */}
                    <div className="p-5 bg-black/20 border-b border-white/5">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 space-y-2 sm:space-y-0">
                            <Heading level={4} className="text-base font-semibold flex items-center">
                                <Icon name="rocket" size="sm" className="mr-2 text-[#7B61FF]" />
                                AI Prompt
                            </Heading>
                            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
                                <Button
                                    onClick={(e) => { e.stopPropagation(); onCopy(); }}
                                    variant={isCopied ? "secondary" : "primary"}
                                    size="sm"
                                    className={`w-full sm:w-auto ${isCopied ? 'bg-[#7B61FF] hover:bg-[#7B61FF]/80 border-[#7B61FF] text-white' : ''}`}
                                >
                                    <Icon name={isCopied ? "check" : "clipboard"} size="xs" className="mr-1" />
                                    {isCopied ? 'Copied!' : 'Copy Prompt'}
                                </Button>
                                <Button
                                    onClick={(e) => { e.stopPropagation(); onShowModal(); }}
                                    variant="primary"
                                    size="sm"
                                    className="w-full sm:w-auto"
                                >
                                    <Icon name="eye" size="xs" className="mr-1" />
                                    View Details
                                </Button>
                            </div>
                        </div>
                        <div className="bg-[#0A0A0A] rounded-lg p-4 border border-[#3A3D46]">
                            <div className="p-4 bg-black/40 rounded-xl border border-white/5 font-mono text-sm text-gray-300 whitespace-pre-wrap leading-relaxed shadow-inner">
                                {cleanPromptText(prompt)}
                            </div>
                        </div>
                    </div>

                    {/* Details Section */}
                    <div className="grid md:grid-cols-2 gap-6 p-5">
                        {/* Left Column */}
                        <div className="space-y-4">
                            {explanation && (
                                <div>
                                    <Heading level={5} className="font-medium mb-2 flex items-center text-sm">
                                        <Icon name="lightbulb" size="xs" className="mr-2 text-[#7B61FF]" />
                                        Why This Matters
                                    </Heading>
                                    <Text variant="secondary" size="sm" className="leading-relaxed">{explanation}</Text>
                                </div>
                            )}

                            {dependencies && dependencies.length > 0 && (
                                <div>
                                    <Heading level={5} className="font-medium mb-2 flex items-center text-sm">
                                        <Icon name="arrowRight" size="xs" className="mr-2 text-white" />
                                        Prerequisites
                                    </Heading>
                                    <div className="space-y-1">
                                        {dependencies.map((dep, index) => {
                                            const rawId = String(dep || '').trim();
                                            const camelId = rawId.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                                            const resolved = (promptsById && (promptsById[rawId] || promptsById[camelId]))
                                                || (promptsById && Object.values(promptsById).find(p => p?.id === rawId));
                                            const title = resolved?.title || rawId;
                                            return (
                                                <div key={index} className="text-sm flex items-center">
                                                    <span className="w-1.5 h-1.5 bg-[#FFB800] rounded-full mr-2"></span>
                                                    <Text variant="secondary" size="sm">{`${index + 1}. ${title}`}</Text>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {outputs && outputs.length > 0 && (
                                <div>
                                    <Heading level={5} className="font-medium mb-2 flex items-center text-sm">
                                        <Icon name="document" size="xs" className="mr-2 text-[#7B61FF]" />
                                        Expected Outputs
                                    </Heading>
                                    <div className="space-y-1">
                                        {outputs.map((output, index) => (
                                            <div key={index} className="text-sm flex items-center">
                                                <span className="w-1.5 h-1.5 bg-[#7B61FF] rounded-full mr-2"></span>
                                                <Text variant="secondary" size="sm">{output}</Text>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {implementationDetails && Object.keys(implementationDetails).length > 0 && (
                                <div>
                                    <Heading level={5} className="font-medium mb-2 flex items-center text-sm">
                                        <Icon name="wrench" size="xs" className="mr-2 text-[#7B61FF]" />
                                        Implementation Details
                                    </Heading>
                                    <div className="space-y-1">
                                        {Object.entries(implementationDetails).map(([key, val]) => (
                                            <div key={key} className="text-sm">
                                                <Text variant="secondary" size="sm"><strong>{key}:</strong> {typeof val === 'string' ? val : JSON.stringify(val)}</Text>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right Column */}
                        <div className="space-y-4">
                            {aftercare && aftercare.length > 0 && (
                                <div>
                                    <Heading level={5} className="font-medium mb-2 flex items-center text-sm">
                                        <Icon name="check" size="xs" className="mr-2 text-white" />
                                        After Completion
                                    </Heading>
                                    <div className="space-y-2">
                                        {aftercare.map((step, index) => (
                                            <div key={index} className="text-sm flex items-start">
                                                <div className="inline-flex items-center justify-center w-4 h-4 bg-[#2A2D36] text-white border border-[#3A3D46] rounded-full text-xs font-medium mr-2 mt-0.5 flex-shrink-0">
                                                    {index + 1}
                                                </div>
                                                <Text variant="secondary" size="sm">{step}</Text>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {validationCriteria && validationCriteria.length > 0 && (
                                <div>
                                    <Heading level={5} className="font-medium mb-2 flex items-center text-sm">
                                        <Icon name="target" size="xs" className="mr-2 text-[#7B61FF]" />
                                        Success Validation
                                    </Heading>
                                    <div className="space-y-1">
                                        {validationCriteria.map((criteria, index) => (
                                            <label key={index} className="flex items-center text-sm cursor-pointer hover:bg-[#2A2D36]/50 p-1 rounded">
                                                <input
                                                    type="checkbox"
                                                    className="mr-2 text-[#7B61FF] rounded border-[#3A3D46] bg-[#2A2D36] focus:ring-[#7B61FF]"
                                                />
                                                <Text variant="secondary" size="sm">{criteria}</Text>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {qualityRequirements && Object.keys(qualityRequirements).length > 0 && (
                                <div>
                                    <Heading level={5} className="font-medium mb-2 flex items-center text-sm">
                                        <Icon name="shield" size="xs" className="mr-2 text-[#7B61FF]" />
                                        Quality Requirements
                                    </Heading>
                                    <div className="space-y-1">
                                        {Object.entries(qualityRequirements).map(([key, val]) => (
                                            <div key={key} className="text-sm">
                                                <Text variant="secondary" size="sm"><strong>{key}:</strong> {typeof val === 'string' ? val : JSON.stringify(val)}</Text>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Prompt Modal Component (RESTORED ORIGINAL FUNCTIONALITY)
const PromptModal = ({ promptData, platform, onClose, onCopy, cleanPromptText, getConfidenceColor }) => {
    const { title, description, prompt, explanation, aftercare, validationCriteria } = promptData;
    const unifiedConfidence0To1_MODAL = (promptData?.confidence || promptData?.metadata?.claudeConfidence || 0.85);
    const confidenceScore = Math.round(unifiedConfidence0To1_MODAL * 100);

    // Enhanced modal content (ORIGINAL FEATURE)
    let modalContent = cleanPromptText(prompt);

    if (explanation) {
        modalContent += `\n\n## Why This Matters\n${explanation}`;
    }

    if (aftercare && aftercare.length > 0) {
        modalContent += `\n\n## After Completion - What to Do Next\n`;
        aftercare.forEach((step, index) => {
            modalContent += `${index + 1}. ${step}\n`;
        });
    }

    if (validationCriteria && validationCriteria.length > 0) {
        modalContent += `\n\n## Success Validation\nCheck these to ensure proper implementation:\n`;
        validationCriteria.forEach((criteria) => {
            modalContent += `☐ ${criteria}\n`;
        });
    }

    const handleCopyModal = async () => {
        try {
            await navigator.clipboard.writeText(modalContent);
        } catch (error) {
            if (import.meta.env.DEV) {
            }
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <Card className="rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-6 border-b border-[#3A3D46]">
                    <div>
                        <Heading level={3} className="text-xl font-bold">{title}</Heading>
                        <Text variant="secondary" size="sm">{description}</Text>
                    </div>
                    <Button onClick={onClose} variant="ghost" size="sm" className="text-[#8B8E98] hover:text-white text-2xl p-1">
                        ×
                    </Button>
                </div>
                <div className="p-6 overflow-y-auto max-h-[70vh]">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 space-y-2 sm:space-y-0">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                            <div className="flex items-center space-x-2">
                                <Text variant="secondary" size="sm">Confidence:</Text>
                                <Badge variant={getConfidenceColor(confidenceScore)} size="sm">
                                    {confidenceScore}%
                                </Badge>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Text variant="secondary" size="sm">Optimized for:</Text>
                                <Badge variant="info" size="sm">
                                    {platform.charAt(0).toUpperCase() + platform.slice(1)}
                                </Badge>
                            </div>
                        </div>
                        <Button
                            onClick={handleCopyModal}
                            variant="primary"
                            size="sm"
                            className="w-full sm:w-auto"
                        >
                            <Icon name="clipboard" size="xs" className="mr-1" />
                            Copy Prompt
                        </Button>
                    </div>
                    <div className="bg-[#0A0A0A] border border-[#3A3D46] rounded-lg p-4">
                        <pre className="text-sm text-[#CCCCCC] whitespace-pre-wrap">{modalContent}</pre>
                    </div>
                </div>
            </Card>
        </div>
    );
};

// Toast Container Component (RESTORED ORIGINAL FUNCTIONALITY)
const ToastContainer = ({ toasts }) => (
    <div className="fixed top-4 right-4 space-y-2 z-50">
        {toasts.map(toast => (
            <div
                key={toast.id}
                className={`px-6 py-3 rounded-lg shadow-lg transform transition-transform duration-300 ${toast.type === 'error' ? 'bg-[#2A2D36] border border-[#FF6B6B] text-[#FF6B6B]' :
                    toast.type === 'warning' ? 'bg-[#2A2D36] border border-[#FFB800] text-[#FFB800]' :
                        toast.type === 'success' ? 'bg-[#2A2D36] border border-white text-white' :
                            'bg-[#2A2D36] border border-[#3A3D46] text-white'
                    }`}
            >
                {toast.message}
            </div>
        ))}
    </div>
);

export default AnalyzePromptGenerator; 